import { Icon, Text, useIsBreakpointLargeAndUp } from '@afterpaytouch/core'
import { useTranslation } from '../../utils/convergence'
import React, { FunctionComponent, useEffect, useState } from 'react'
import {
  useAppRouter,
  useCountry,
  useOfferDetailPBI,
  usePBIRejected,
  usePBIUnavailable,
  useAppropriatePaymentMethod,
  usePaymentOptionsError,
  selectPurchaseMethod,
  setCheckoutStatus,
  useAppDispatch,
  usePaymentOptions,
  useAddPaymentMethodFlowExperimentTrigger,
  useShouldRoutePi4SwitcherWithNoPaymentMethodToSummary,
  useShouldTriggerPi4SwitcherWithNoPaymentMethodExperiment,
} from '../../state'
import { useCheckoutSizes } from '../../utils/responsive'
import { usePBIRejectedText } from './helper'
import styles from './style.module.css'
import { Country, PaymentType } from '@afterpay/types'
import { Route } from '../../config/router'
import { useMonitoringWithEnduringEventProperties } from '../../integrations/monitoring'
import { TrackingEvent } from '../../model/monitoring'
import { CheckoutStatus } from '../../model'

import { ConsumerWelcome } from './ConsumerWelcome'
import { PaymentTypesPBIRejected } from './PaymentTypesPBIRejected'
import { PaymentTypesRadioGroup } from './PaymentTypesRadioGroup'
import { PaymentTypesTerms } from './PaymentTypesTerms'
import { PaymentTypesContinueButton } from './PaymentTypesContinueButton'

const PBIRejectedRadioMessage = (): JSX.Element => {
  const { smallTextSize } = useCheckoutSizes()
  const rejectedTextPBI = usePBIRejectedText()

  return (
    <div className={styles.PBIDeclineWrapper} data-testid='pbi-decline-wrapper'>
      <span className={styles.rejectedInfoIcon}>
        <Icon.Info size='ExtraSmall' />
      </span>
      <span className={styles.rejectedText}>
        <Text size={smallTextSize}>{rejectedTextPBI}</Text>
      </span>
    </div>
  )
}

export const PaymentTypes: FunctionComponent = () => {
  const { t } = useTranslation()
  const isDesktop = useIsBreakpointLargeAndUp()
  const dispatch = useAppDispatch()
  const router = useAppRouter()
  const hasPBIRejected = usePBIRejected()
  const isPbiUnavailable = usePBIUnavailable()
  const offerDetailPBI = useOfferDetailPBI()
  const { logEvent } = useMonitoringWithEnduringEventProperties()
  const country = useCountry()
  const paymentMethod = useAppropriatePaymentMethod()
  const paymentOptionsError = usePaymentOptionsError()
  const paymentOptions = usePaymentOptions()
  const shouldRoutePi4SwitcherWithNoPaymentMethodToSummary = useShouldRoutePi4SwitcherWithNoPaymentMethodToSummary(PaymentType.PBI)
  const shouldTriggerPi4SwitcherWithNoPaymentMethodExperiment = useShouldTriggerPi4SwitcherWithNoPaymentMethodExperiment(PaymentType.PBI)
  const triggerAddPaymentMethodFlowExperiment = useAddPaymentMethodFlowExperimentTrigger()

  useEffect(() => {
    if (paymentOptions?.length > 0) {
      router.push(Route.PAYMENT_OPTIONS)
    }
  }, [])
  const [pbiOptionSelected, setPbiOptionSelected] = useState<boolean>(false)

  useEffect(() => {
    if (hasPBIRejected) {
      logEvent(TrackingEvent.VIEWED_NON_TERMINAL_ERROR, { errorCode: offerDetailPBI?.rejectedReasonCode })
    }
  }, [])

  useEffect(() => {
    router.prefetch(Route.PAYMENT_OPTIONS)
    router.prefetch(Route.ADD_PAYMENT_METHOD)
    router.prefetch(Route.SUMMARY)
  }, [])

  useEffect(() => {
    if (pbiOptionSelected || paymentOptionsError !== undefined) {
      if (paymentMethod !== null) {
        dispatch(selectPurchaseMethod(paymentMethod, false))
      } else {
        if (pbiOptionSelected && shouldTriggerPi4SwitcherWithNoPaymentMethodExperiment) {
          triggerAddPaymentMethodFlowExperiment({ source: 'cl_entry_pi4_switcher', selectedPaymentType: PaymentType.PBI })
          if (shouldRoutePi4SwitcherWithNoPaymentMethodToSummary) {
            router.push(Route.SUMMARY)
            return
          }
        }
        dispatch(setCheckoutStatus(CheckoutStatus.LackOfEligiblePaymentMethod))
        router.push(Route.ADD_PAYMENT_METHOD)
        return
      }
      router.push(Route.SUMMARY)
    }
  }, [pbiOptionSelected, paymentOptionsError])

  const showPBIRejectedView = isPbiUnavailable && country === Country.US

  return (
    <>
      {showPBIRejectedView && <PaymentTypesPBIRejected />}
      {!showPBIRejectedView && (
        <>
          <ConsumerWelcome />
          <div className={isDesktop ? styles.paymentTypesWrapperDesktop : styles.paymentTypesWrapperMobile}>
            <PaymentTypesRadioGroup />
            {isPbiUnavailable && <PBIRejectedRadioMessage />}
            <PaymentTypesTerms />
            <PaymentTypesContinueButton setPbiOptionSelected={setPbiOptionSelected} />
          </div>
        </>
      )}
    </>
  )
}
